import { useGlobalStore } from '@/stores';
import { formatMessage, showErrorMessage, showSuccessMessage } from './Notification';

const baseUrl = () => {
    const parts = window.location.href.split('/');
    return parts.slice(0, 4).join('/');
};

const clearStatus = () => {
    const store = useGlobalStore();
    store.setLoadingRequests(-1);
    if (store.showLoadingRequests === 0) {
        store.hideLoadingOverlay();
    }
};

const clearStatusMessage = () => {
    const store = useGlobalStore();
    store.hideStatusMessage();
};

const resetStore = () => {
    const store = useGlobalStore();
    const lang = store.requestParams.lang;
    store.$reset();
    if (lang) {
        store.setRequestParams({ lang });
    }
};

const timeout = () => {
    const store = useGlobalStore();
    const sso = store.user.sso;
    resetStore();
    if (sso) {
        redirect('message/3');
    } else {
        redirect('login');
    }
};

const logout = (router) => {
    const promise = post('main/logout', {});
    promise.then((response) => {
        resetStore();
        if (response.close) {
            router.push('/message/4');
        } else {
            router.push('/login');
        }
    });
    return promise;
};

// relative redirect
const redirect = (url) => {
    // eslint-disable-next-line no-undef
    window.location.assign(__webpack_public_path__ + url);
};

/**
 * Perform HTTP post and return promise.
 *
 * @param {string}        target - The URL to post to.
 * @param {string|Object} params - The values to post. Either a URL encoded string or an object of parameters.
 * @param {function}      onSuccess
 * @return Promise
 */
const deleteItems = (target, params = {}, onSuccess = null) => {
    clearStatusMessage();
    const deleteSuccess = (response) => {
        showSuccessMessage(formatMessage('Delete successful'));
        if (onSuccess) {
            onSuccess(response);
        }
    };

    return load(target, params, deleteSuccess, 'Deleting...');
};

/**
 * Perform HTTP post and return promise.
 *
 * @param {string}        target    - The URL to post to.
 * @param {string|Object} params    - The values to post. Either a URL encoded string or an object of parameters.
 * @param {function}      onSuccess
 * @param {string}        text      - loading overlay text
 * @return Promise
 */
const load = (target, params = {}, onSuccess = null, text = 'Loading...') => {
    showStatus(text);
    const promise = post(target, params, onSuccess);
    promise.then((response) => {
        clearStatus();
        if (response && typeof response.status !== 'undefined') {
            if (response.status === 'error') {
                showErrorMessage(formatMessage(response.errorMessage));
            } else if (response.status === 'alert') {
                if (response.errorMessages) {
                    alert(response.errorMessages.map((x) => formatMessage(x)).join('\r\n'));
                } else {
                    alert(formatMessage(response.errorMessage));
                }
            }
        }
    });

    return promise;
};

// Convert object to URL encoded string
const objectToUrlParams = (data) => {
    return Object.keys(data)
        .map((k) => {
            if (data[k] instanceof Array) {
                // if array then parse to urlencoded array eg. a:['b','c'] to a[0]=b&a[1]=c
                let parts = [];
                for (let i in data[k]) {
                    parts.push(encodeURIComponent(k + '[' + i.toString() + ']') + '=' + encodeURIComponent(data[k][i]));
                }
                return parts.join('&');
            } else if (data[k] !== null) {
                // if not array then just join key and value with =
                return encodeURIComponent(k) + '=' + encodeURIComponent(data[k]);
            }
        })
        .filter((k) => k)
        .join('&');
};

/**
 * Perform HTTP post and return promise.
 *
 * @param {string}        target - The URL to post to.
 * @param {string|Object} params - The values to post. Either a URL encoded string or an object of parameters.
 * @param {function}      onSuccess
 * @return Promise
 */
const post = (target, params = {}, onSuccess) => {
    const requestKey = JSON.stringify({ target, params });

    // if object then convert to url encoded string
    if (typeof params === 'object') {
        params = objectToUrlParams(params);
    }

    const store = useGlobalStore();
    params = params + (params === '' ? '' : '&') + 'szSecurityToken=' + store.securityToken;
    params = params + '&' + objectToUrlParams(store.requestParams) + '&appUrl=' + baseUrl();

    // Setup default options for the request
    let options = {};
    options.headers = { 'Content-type': 'application/x-www-form-urlencoded' };
    let caught = false;
    store.addRequest(requestKey);
    // eslint-disable-next-line no-undef
    const prefix = process.env.APP_API_PATH;
    const promise = fetch(prefix + target, { ...options, method: 'POST', body: params }).then((response) => {
        store.removeRequest(requestKey);
        if (response.status === 200) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return response.json();
            }

            return;
        }

        caught = true;
        clearStatus();
        if (response.status === 401) {
            timeout();
            return;
        }

        if (response.status === 0) {
            // The response is in an error state due to us aborting the request, so don't flag this as an error
            return;
        }

        if (response.statusText) {
            showErrorMessage(response.statusText);
        }
    });
    promise.then((response) => {
        if (caught) {
            return;
        }

        if (!response || typeof response.status === 'undefined') {
            // if no status in response then session may have expired - reload page to trigger redirect to login page
            timeout();
        }

        if (response?.sessionExpiry) {
            store.setUserSessionDuration(response.sessionExpiry);
        }

        if (response?.sessionLastAccessedTime) {
            store.setUserSessionLastAccessed(response.sessionLastAccessedTime);
        }

        if (response && response.status === 'OK' && onSuccess) {
            onSuccess(response);
        }
    });

    return promise;
};

/**
 * Perform HTTP post and return promise.
 *
 * @param {string}        target - The URL to post to.
 * @param {string|Object} params - The values to post. Either a URL encoded string or an object of parameters.
 * @param {function}      onSuccess
 * @return Promise
 */
const save = (target, params, onSuccess) => {
    clearStatusMessage();
    const saveSuccess = (response) => {
        showSuccessMessage(formatMessage('Update successful'));
        if (onSuccess) {
            onSuccess(response);
        }
    };

    return load(target, params, saveSuccess, 'Saving...');
};

const get = (target, onSuccess) => {
    return fetch(target).then((response) => {
        if (response && response.status !== 'undefined' && response.status === 200) {
            onSuccess(response);
        }
    });
};

// show loading overlay
const showStatus = (text) => {
    const store = useGlobalStore();
    store.setLoadingRequests(1);
    store.setShowLoadingOverlay(text);
};

export { baseUrl, deleteItems, load, logout, post, save, get, redirect, resetStore };
